import {FC, forwardRef, ReactNode, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {toast} from 'react-toastify';
//formik
import {Formik, FormikProps, useFormikContext} from 'formik';
import {CreateEventFormValues, initialValues, validationSchema} from './formConfiguration';
//mui
import {Box} from '@mui/material';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getEvents, getSponsorCategoriesSelector} from '@/store/organization-event';
//context
import {CreateNewEventContext, CreateNewEventContextType} from './OrganizationSignUpContext';
//service
import {OrganizationEventService} from '@/utils/services/organization-event.service';
//helpers
import {CREATE_EVENT_STEP, createEventSteps, getNewEventValues} from './helpers';
import {getErrorMessage} from '@/utils/helpers';
import {sendPresignedImage} from '@/pages/Organization/Profile/General/ChangePhotoModal';
import {useGetOrganizationId} from '@/utils/hooks';
//types
import {IOrganizationEventRequest} from '@/utils/types';
//constants
import {DONE, FILLED} from '@/utils/constants';
//components
import Stepper from '@/shared/components/Stepper';
import EventSetting from './EventSetting';
import TableAndTickets from './TableAndTickets';
import EventWebpage from './EventWebpage';
import Modal from '@/shared/components/Modal';
//icons
import {ReactComponent as CheckIcon} from '@/assets/icons/rudiobutton-checked.svg';
import {ReactComponent as WarningIcon} from '@/assets/icons/warning.svg';
import {EVENT_IMAGE} from '@/utils/enums';
//styles
import {createNewEventStyle} from './CreateNewEvent.styles';
import {resolveCategoryId} from './EventSetting/EventSponsors/AddSponsorModalForm/helpers';
import {profileContactListStyle} from '@/pages/Organization/Profile/ContactList/ProfileContactList.styles';

interface CreateNewEventProps {
  onCloseModal: () => void;
}

export interface CreateNewEventHandle {
  requestClose: () => void;
}
export const getEventImage = async (logo: File, type: EVENT_IMAGE) => {
  const presignedLogoUrl = await OrganizationEventService.presignedImage(type);

  await sendPresignedImage(presignedLogoUrl.data.url, logo);

  return presignedLogoUrl.data.key.split('/').pop();
};

const CreateNewEvent = forwardRef<CreateNewEventHandle, CreateNewEventProps>(({onCloseModal}, ref) => {
  const dispatch = useAppDispatch();

  const formikRef = useRef<FormikProps<CreateEventFormValues>>(null);

  const [activeStep, setActiveStep] = useState(0);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const organizationId = useGetOrganizationId();
  const sponsorCategories = useAppSelector(getSponsorCategoriesSelector);

  const handleBackStep = () => setActiveStep((prev) => prev - 1);
  const handleNextStep = () => setActiveStep((prev) => prev + 1);

  const formData = useMemo(() => {
    switch (activeStep) {
      case CREATE_EVENT_STEP.SETTING:
        return <EventSetting />;
      case CREATE_EVENT_STEP.TABLES_AND_TICKETS:
        return <TableAndTickets />;
      case CREATE_EVENT_STEP.WEBPAGE:
        return <EventWebpage />;
      default:
        return <EventWebpage />;
    }
  }, [activeStep]);

  const submitClickHandler = async (values: CreateEventFormValues) => {
    if (!organizationId) return;

    if (activeStep === CREATE_EVENT_STEP.WEBPAGE) {
      try {
        const image = await getEventImage(values.logo as File, EVENT_IMAGE.EVENT);
        const raffleImage = values.prizeLogo ? await getEventImage(values.prizeLogo as File, EVENT_IMAGE.RAFFLE) : null;

        const newEventValues: IOrganizationEventRequest = getNewEventValues(values, raffleImage);

        const {data} = await OrganizationEventService.createEvent({
          ...newEventValues,
          image,
          sponsors: [],
        });

        if (data) {
          const existingCategories = sponsorCategories || [];
          const categoriesCache = new Map<string, number>();

          const uniqueCategoryNames = [
            ...new Set(
              values.sponsors.map((s) => s?.category?.name?.trim()).filter((name): name is string => Boolean(name))
            ),
          ];
          for (const categoryName of uniqueCategoryNames) {
            // eslint-disable-next-line no-await-in-loop
            await resolveCategoryId(organizationId, categoryName, existingCategories, categoriesCache);
          }

          for (const sponsor of values.sponsors) {
            // eslint-disable-next-line no-await-in-loop
            const categoryId = await resolveCategoryId(
              organizationId,
              sponsor?.category?.name?.trim() || '',
              existingCategories,
              categoriesCache
            );

            // eslint-disable-next-line no-await-in-loop
            const presignedUrl = await OrganizationEventService.presignedImage(EVENT_IMAGE.SPONSOR);
            // eslint-disable-next-line no-await-in-loop
            await sendPresignedImage(presignedUrl.data.url, sponsor.logo);

            const sponsorsImageData = {
              name: sponsor.name,
              logo: presignedUrl.data.key.split('/').pop() as string,
              categoryId,
              sortOrder: sponsor.sortOrder,
            };

            // eslint-disable-next-line no-await-in-loop
            await OrganizationEventService.addSponsorToEvent(data, sponsorsImageData);
          }
        }

        setIsSuccessModalOpen(true);

        try {
          await dispatch(getEvents(organizationId)).unwrap();
        } catch (refreshError) {
          console.error('Failed to refresh events list', refreshError);
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    } else {
      handleNextStep();
    }
  };

  const requestClose = () => {
    if (isDirty) {
      setIsExitModalOpen(true);
    } else {
      onCloseModal();
    }
  };

  useImperativeHandle(ref, () => ({requestClose}));

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={submitClickHandler}
      validationSchema={validationSchema(activeStep)}
      validateOnChange={true}
    >
      <CreateNewEventContent
        activeStep={activeStep}
        formData={formData}
        onCloseModal={onCloseModal}
        handleBackStep={handleBackStep}
        handleNextStep={handleNextStep}
        isSuccessModalOpen={isSuccessModalOpen}
        setIsSuccessModalOpen={setIsSuccessModalOpen}
        isExitModalOpen={isExitModalOpen}
        setIsExitModalOpen={setIsExitModalOpen}
        requestClose={requestClose}
        onDirtyChange={setIsDirty}
      />
    </Formik>
  );
});

interface CreateNewEventContentProps {
  activeStep: number;
  formData: ReactNode;
  onCloseModal: () => void;
  handleBackStep: () => void;
  handleNextStep: () => void;
  isSuccessModalOpen: boolean;
  setIsSuccessModalOpen: (v: boolean) => void;
  isExitModalOpen: boolean;
  setIsExitModalOpen: (v: boolean) => void;
  requestClose: () => void;
  onDirtyChange: (v: boolean) => void;
}

const CreateNewEventContent: FC<CreateNewEventContentProps> = ({
  activeStep,
  formData,
  onCloseModal,
  handleBackStep,
  handleNextStep,
  isSuccessModalOpen,
  setIsSuccessModalOpen,
  isExitModalOpen,
  setIsExitModalOpen,
  requestClose,
  onDirtyChange,
}) => {
  const {
    dirty,
  } = useFormikContext<CreateEventFormValues>();


  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const handleExitModalChange = () => setIsExitModalOpen(!isExitModalOpen);

  const CreateNewEventContextValue: CreateNewEventContextType = useMemo(
    () => ({
      activeStep,
      onCloseModal: requestClose,
      //   closeCreateEventModal,
      handleBackStep,
      handleNextStep,
    }),
    [activeStep, requestClose]
  );

  return (
    <CreateNewEventContext.Provider value={CreateNewEventContextValue}>
      <Box sx={createNewEventStyle.wrapper}>
        <Stepper steps={createEventSteps} activeStep={activeStep} sx={createNewEventStyle.stepper} />
        {formData}

        <Modal
          title="Event Created"
          description="Your event has been successfully created."
          mainIcon={<CheckIcon />}
          closeButtonTitle={DONE}
          onClose={onCloseModal}
          open={isSuccessModalOpen}
          isShowCloseButton
          closeButtonVariant={FILLED}
          sx={createNewEventStyle.modal}
        />
        <Modal
          open={isExitModalOpen}
          onClose={handleExitModalChange}
          onSave={() => {
            handleExitModalChange();
            onCloseModal();
          }}
          sx={profileContactListStyle.deleteContactModal}
          title="Exit without saving?"
          description="Are you sure you want to exit? Your current information will not be saved."
          mainButtonTitle="Exit"
          closeButtonTitle="Cancel"
          mainIcon={<WarningIcon />}
          color="warning"
          isShowSaveButton
          isShowCloseButton
        />
      </Box>
    </CreateNewEventContext.Provider>
  );
};

export default CreateNewEvent;
